/**
 * Display name validation utilities.
 *
 * Uses shared validation constants from constants.ts which match
 * the backend validation in src/responsum/room/validation.py
 */

import { VALIDATION } from "./constants";

const { min_length: MIN_LENGTH, max_length: MAX_LENGTH, dangerous_chars: DANGEROUS_CHARS } =
  VALIDATION.display_name;

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Check if a display name contains dangerous characters.
 *
 * @param name - The display name to check
 * @returns The first dangerous character found, or null if none
 */
export function findDangerousChar(name: string): string | null {
  for (const char of DANGEROUS_CHARS) {
    if (name.includes(char)) {
      return char === "\x00" ? "null byte" : char;
    }
  }
  return null;
}

/**
 * Validate a display name.
 *
 * @param name - The display name to validate
 * @returns Validation result with error message if invalid
 */
export function validateDisplayName(name: string): ValidationResult {
  // Check for empty/whitespace-only
  const trimmed = name.trim();
  if (trimmed.length < MIN_LENGTH) {
    return { valid: false, error: "Display name is required" };
  }

  // Check length
  if (trimmed.length > MAX_LENGTH) {
    return { valid: false, error: `Display name must be ${MAX_LENGTH} characters or less` };
  }

  // Check for dangerous characters
  const dangerousChar = findDangerousChar(name);
  if (dangerousChar) {
    return {
      valid: false,
      error: `Display name cannot contain ${dangerousChar === "null byte" ? "null bytes" : `'${dangerousChar}'`}`,
    };
  }

  return { valid: true };
}

/**
 * Sanitize a display name by removing dangerous characters.
 * Use validateDisplayName() for user feedback; this is for fallback sanitization.
 *
 * @param name - The display name to sanitize
 * @returns Sanitized display name
 */
export function sanitizeDisplayName(name: string): string {
  let sanitized = name;
  for (const char of DANGEROUS_CHARS) {
    sanitized = sanitized.split(char).join("");
  }
  return sanitized.trim().slice(0, MAX_LENGTH);
}
